import React, { useState, useEffect, useRef, useCallback } from 'react';
import { subDays, format } from 'date-fns';
import { useAuth } from '../../auth/AuthProvider';
import Header from '../../components/Header';
import { styles, colors, radius, shadows, typography } from '../../styles/theme';
import { invalidateQueries } from '../../hooks/useCachedQuery';
import { normalizeForSearch, searchMatchPre } from '../../utils/searchMatch';
import { useToast } from '../../components/Toast';
import useUrlState from '../../hooks/useUrlState';
import SearchableSelect from '../../components/SearchableSelect';
import EnterNavForm from '../../components/EnterNavForm';
import { parseTutarTR, formatTutarTR } from '../../utils/para';
import { logError } from '../../utils/logError';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

// Kalem satırlarına stabil benzersiz id ver — index key + stateful child (autocomplete)
// ortada satır silinince yanlış instance'ı yeniden kullanır. uid ile her satır kendi state'ini taşır.
let _kalemUidCounter = 0;
const makeKalem = (kalem = '', tutar = '') => ({ uid: ++_kalemUidCounter, kalem, tutar });

// FIRMA_OPTIONS artık DB'den (/api/firmalar) geliyor — Ayarlar > Firmalar'dan yönetilir.

// KATEGORI_OPTIONS artık DB'den (/api/gider-kategorileri) geliyor — Ayarlar > Gider Kategorileri'nden yönetilir.
// Fallback (API erişilemezse): seed verisi.
const KATEGORI_OPTIONS_FALLBACK = [
  { value: 'KIRA_STOPAJ', label: 'Kira ve Stopaj', renk: '#0d6efd' },
  { value: 'PERSONEL', label: 'Personel', renk: '#6610f2' },
  { value: 'ENERJI', label: 'Enerji', renk: '#ffc107' },
  { value: 'PAZARLAMA_REKLAM', label: 'Pazarlama ve Reklam', renk: '#fd7e14' },
  { value: 'LOJISTIK', label: 'Lojistik', renk: '#198754' },
  { value: 'BAKIM_ONARIM', label: 'Bakım ve Onarım', renk: '#20c997' },
  { value: 'SARF_TEMIZLIK', label: 'Sarf ve Temizlik', renk: '#0dcaf0' },
  { value: 'FINANSMAN', label: 'Finansman', renk: '#dc3545' },
  { value: 'TEMSIL_AGIRLAMA', label: 'Temsil ve Ağırlama', renk: '#d63384' },
  { value: 'TELEKOMUNIKASYON', label: 'Telekomünikasyon', renk: '#6f42c1' },
  { value: 'DIJITAL_SOSYAL_MEDYA', label: 'Dijital ve Sosyal Medya', renk: '#e83e8c' },
  { value: 'DEMIRBAS', label: 'Demirbaş', renk: '#607d8b' },
  { value: 'GENEL_YONETIM', label: 'Genel Yönetim Giderleri', renk: '#3949ab' },
  { value: 'DIGER', label: 'Diğer', renk: '#6c757d' },
];

const ODEME_OPTIONS = [
  { value: '', label: 'Seçiniz' },
  { value: 'NAKIT', label: 'Nakit' },
  { value: 'HAVALE', label: 'Havale / EFT' },
  { value: 'KREDI_KARTI', label: 'Kredi Kartı' },
  { value: 'CEK', label: 'Çek' },
  { value: 'SENET', label: 'Senet' },
  { value: 'ACIK_HESAP', label: 'Açık Hesap' },
];

// KREDI_KARTI_OPTIONS artık DB'den (/api/kredi-kartlari) geliyor — Ayarlar > Kredi Kartları'ndan yönetilir.
// Fallback (API erişilemezse): seed verisi.
const KREDI_KARTI_OPTIONS_FALLBACK = [
  { value: 'KART_9577', label: '9577' },
  { value: 'KART_0662', label: '0662' },
  { value: 'KART_5238', label: '5238' },
  { value: 'KART_1922', label: '1922' },
  { value: 'KART_0965', label: '0965' },
];

// ==================== TOGGLE ====================
const Toggle = ({ checked, onChange, label, title, activeColor = colors.primary }) => {
  const [focused, setFocused] = useState(false);
  return (
    <label title={title} style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '14px', color: colors.textPrimary, background: colors.backgroundAlt, border: `1px solid ${colors.border}`, borderRadius: radius.sm, padding: '7px 12px' }}>
      <span role="switch" aria-checked={checked} style={{ position: 'relative', width: '34px', height: '20px', borderRadius: radius.full, background: checked ? activeColor : '#cbd5e1', transition: 'background .15s', flexShrink: 0, boxShadow: focused ? shadows.glow : 'none' }}>
        <span style={{ position: 'absolute', top: '2px', left: checked ? '16px' : '2px', width: '16px', height: '16px', borderRadius: '50%', background: '#fff', transition: 'left .15s' }} />
      </span>
      <input type="checkbox" checked={checked} onChange={onChange} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }} />
      {label}
    </label>
  );
};

// ==================== KALEM AUTOCOMPLETE ====================
const GiderKalemAutocomplete = ({ value, onChange, kategori, onEnter }) => {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showList, setShowList] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => { setQuery(value || ''); }, [value]);

  // Öneriler değişince / liste kapanınca aktif satırı sıfırla.
  useEffect(() => { setActiveIndex(-1); }, [suggestions]);
  useEffect(() => { if (!showList) setActiveIndex(-1); }, [showList]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowList(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSuggestions = useCallback((q) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const token = localStorage.getItem('access_token');
      let url = `${API_URL}/api/finance/gider/kalem-onerileri?q=${encodeURIComponent(q)}&limit=15`;
      if (kategori) url += `&kategori=${kategori}`;
      try {
        const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
        const data = await res.json();
        setSuggestions(Array.isArray(data) ? data : []);
      } catch {
        setSuggestions([]);
      }
    }, 300);
  }, [kategori]);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    onChange(val);
    if (val.length >= 1) {
      fetchSuggestions(val);
      setShowList(true);
    } else {
      setSuggestions([]);
      setShowList(false);
    }
  };

  const handleSelect = (item) => {
    setQuery(item);
    onChange(item);
    setShowList(false);
  };

  const handleFocus = () => {
    if (query.length >= 1) {
      fetchSuggestions(query);
      setShowList(true);
    } else {
      // Bos iken tum onerileri getir
      fetchSuggestions('');
      setShowList(true);
    }
  };

  const handleKeyDown = (e) => {
    const listOpen = showList && suggestions.length > 0;
    if (e.key === 'ArrowDown') {
      if (listOpen) { e.preventDefault(); setActiveIndex(i => Math.min(i + 1, suggestions.length - 1)); }
    } else if (e.key === 'ArrowUp') {
      if (listOpen) { e.preventDefault(); setActiveIndex(i => Math.max(i - 1, 0)); }
    } else if (e.key === 'Enter') {
      if (listOpen && activeIndex >= 0) {
        // Liste açıkken Enter formu kaydetmez, öneriyi seçer.
        e.preventDefault();
        handleSelect(suggestions[activeIndex]);
      } else if (onEnter) {
        onEnter();
      }
    } else if (e.key === 'Escape') {
      setShowList(false);
    }
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <input
        type="text"
        value={query}
        onChange={handleChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder="Gider kalemi yazın (örn: Elektrik faturası)"
        style={inputStyle}
      />
      {showList && suggestions.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0,
          background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: '0 0 6px 6px',
          maxHeight: '180px', overflowY: 'auto', zIndex: 999,
          boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
        }}>
          {suggestions.map((s, i) => (
            <div
              key={i}
              onClick={() => handleSelect(s)}
              style={{
                padding: '8px 12px', cursor: 'pointer', fontSize: '13px',
                borderBottom: '1px solid #f0f0f0',
                background: i === activeIndex
                  ? colors.infoLight
                  : (query.toLowerCase() === s.toLowerCase() ? '#e3f2fd' : 'transparent')
              }}
              onMouseEnter={() => { setActiveIndex(i); }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = query.toLowerCase() === s.toLowerCase() ? '#e3f2fd' : 'transparent';
              }}
            >
              {s}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== MAIN COMPONENT ====================
const GiderGirisiPage = () => {
  useAuth();
  const toast = useToast();
  const [firmalar, setFirmalar] = useState([]);
  const [lokasyonlar, setLokasyonlar] = useState([]);
  const [tedarikciler, setTedarikciler] = useState([]);
  const [kategoriler, setKategoriler] = useState(KATEGORI_OPTIONS_FALLBACK);
  const [kartlar, setKartlar] = useState(KREDI_KARTI_OPTIONS_FALLBACK);
  const [stopajTurleri, setStopajTurleri] = useState([]);
  const [firma, setFirma] = useState('');
  const [kategori, setKategori] = useState('');
  // Stopaj: brütten gelir-vergisi stopajı türetme. '' = stopaj yok.
  // Oran StopajTuru.oran'dan (yüzde). brüt=kaydedilen tutar; backend stopajı brütten hesaplar.
  const [stopajTuru, setStopajTuru] = useState('');
  const [stopajGiris, setStopajGiris] = useState('BRUT'); // 'BRUT' | 'NET'
  const [kalemler, setKalemler] = useState(() => [makeKalem()]);
  const [notlar, setNotlar] = useState('');
  const [giderTarihi, setGiderTarihi] = useState(new Date().toISOString().split('T')[0]);
  const [faturali, setFaturali] = useState(false);
  // SGK/Vergi tahakkuku: ödeme yapılmamış olsa bile gider DOĞDUĞU aya yazılır
  // (tahakkuk esası — yönetim paneli net kârı yanıltmasın). backend kaynak_tipi=VERGI_TAHAKKUK.
  const [vergiTahakkuku, setVergiTahakkuku] = useState(false);
  const [faturaNo, setFaturaNo] = useState('');
  const [odemeYontemi, setOdemeYontemi] = useState('');
  const [odemeDetay, setOdemeDetay] = useState('');
  const [vadeTarihi, setVadeTarihi] = useState('');
  const [lokasyonId, setLokasyonId] = useState('');
  const [tedarikciId, setTedarikciId] = useState('');
  // Tedarikçili ödenmiş alımı cari ekstrede "ödenmiş alış" göster (opt-in, ACIK_HESAP dışı)
  const [cariyeIsle, setCariyeIsle] = useState(false);
  const [loading, setLoading] = useState(false);
  const [gecmis, setGecmis] = useState([]);
  const [showGecmis] = useState(true);
  const [gecmisLoading, setGecmisLoading] = useState(false);
  const [editId, setEditId] = useState(null);

  // Geçmiş filtreleri — URL'e bağlı (navigasyon + refresh sonrası yaşar, link paylaşılabilir).
  const [gBas, setGBas] = useUrlState('g_bas', format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [gBit, setGBit] = useUrlState('g_bit', format(new Date(), 'yyyy-MM-dd'));
  const [gFirma, setGFirma] = useUrlState('g_firma', '');
  const [gKat, setGKat] = useUrlState('g_kat', '');
  const [gOdeme, setGOdeme] = useUrlState('g_odeme', '');
  const [gAra, setGAra] = useUrlState('g_ara', '', { debounceMs: 300 });

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    const headers = { 'Authorization': `Bearer ${token}` };
    Promise.all([
      fetch(`${API_URL}/api/firmalar`, { headers }).then(r => r.json()).catch(() => []),
      fetch(`${API_URL}/api/lokasyonlar`, { headers }).then(r => r.json()).catch(() => []),
      fetch(`${API_URL}/api/tedarikciler`, { headers }).then(r => r.json()).catch(() => []),
      // include_inactive=true: pasif kategorilerin de etiketi/rengi düzgün gözüksün eski kayıtlarda
      fetch(`${API_URL}/api/gider-kategorileri?include_inactive=true`, { headers }).then(r => r.json()).catch(() => null),
      // include_inactive=true: pasif kartların da etiketi eski ödeme kayıtlarında düzgün görünsün
      fetch(`${API_URL}/api/kredi-kartlari?include_inactive=true`, { headers }).then(r => r.json()).catch(() => null),
      // Stopaj türleri: yalnız aktif (girişte seçilebilenler). {kod, ad, oran}.
      fetch(`${API_URL}/api/stopaj-turleri`, { headers }).then(r => r.json()).catch(() => []),
    ]).then(([firmaData, lokData, tedData, katData, kartData, stopajData]) => {
      setFirmalar(Array.isArray(firmaData) ? firmaData : []);
      setLokasyonlar(Array.isArray(lokData) ? lokData : []);
      setTedarikciler(Array.isArray(tedData) ? tedData.filter(t => t.aktif !== false) : []);
      // Kategoriler: API'den geldiyse {value, label, renk, aktif} formatına dönüştür; yoksa fallback'i kullan
      if (Array.isArray(katData) && katData.length > 0) {
        setKategoriler(katData.map(k => ({ value: k.kod, label: k.ad, renk: k.renk || '#6c757d', aktif: k.aktif !== false, varsayilan_stopaj_turu_kod: k.varsayilan_stopaj_turu_kod || null })));
      }
      // Kartlar: API'den {kod, son_dort_hane, aktif} → {value, label, aktif}
      if (Array.isArray(kartData) && kartData.length > 0) {
        setKartlar(kartData.map(k => ({ value: k.kod, label: k.son_dort_hane, aktif: k.aktif !== false })));
      }
      setStopajTurleri(Array.isArray(stopajData) ? stopajData : []);
    });
  }, []);

  const resetForm = () => {
    setFirma('');
    setKategori('');
    setKalemler([makeKalem()]);
    setNotlar('');
    setFaturali(false);
    setFaturaNo('');
    setVergiTahakkuku(false);
    setOdemeYontemi('');
    setOdemeDetay('');
    setLokasyonId('');
    setTedarikciId('');
    setCariyeIsle(false);
    setStopajTuru('');
    setStopajGiris('BRUT');
    setEditId(null);
  };

  // Kalem satir islemleri
  const addKalem = () => {
    setKalemler([...kalemler, makeKalem()]);
  };

  const removeKalem = (index) => {
    if (kalemler.length <= 1) return;
    setKalemler(kalemler.filter((_, i) => i !== index));
  };

  const updateKalem = (index, field, value) => {
    const updated = [...kalemler];
    updated[index] = { ...updated[index], [field]: value };
    setKalemler(updated);
  };

  const genelToplam = kalemler.reduce((sum, k) => sum + (parseTutarTR(k.tutar) || 0), 0);

  // Kategori değişince varsayılan stopaj türünü öner (auto-suggest). Kullanıcı değiştirebilir/temizleyebilir.
  // Yalnız aktif bir türe denk gelirse önerilir; aksi halde mevcut seçim korunur.
  const handleKategoriChange = (val) => {
    setKategori(val);
    const kat = kategoriler.find(k => k.value === val);
    const varsayilan = kat?.varsayilan_stopaj_turu_kod || '';
    if (varsayilan && stopajTurleri.some(s => s.kod === varsayilan)) {
      setStopajTuru(varsayilan);
    }
  };

  // Seçili stopaj türü + oran (yüzde). oran>=100 → NET hesaplama imkânsız (sıfıra/negatife bölme).
  const stopajTuruObj = stopajTurleri.find(s => s.kod === stopajTuru) || null;
  const stopajOran = stopajTuruObj ? (Number(stopajTuruObj.oran) || 0) : 0;
  const netHesaplanamaz = stopajGiris === 'NET' && stopajOran >= 100;
  // BRUT modu: girilen tutar brüttür. NET modu: girilen net → brüt = net / (1 - oran/100).
  const stopajBrut = stopajTuru && !netHesaplanamaz
    ? (stopajGiris === 'NET' ? genelToplam / (1 - stopajOran / 100) : genelToplam)
    : genelToplam;
  const stopajTutar = stopajTuru ? stopajBrut * stopajOran / 100 : 0;
  const stopajNet = stopajBrut - stopajTutar;
  // NET modunda her satırın tutarı net girilir → brüte çevrilip kaydedilir (backend brütten hesaplar).
  const brutFactor = (stopajTuru && stopajGiris === 'NET' && !netHesaplanamaz) ? 1 / (1 - stopajOran / 100) : 1;
  const toBrut = (tutar) => parseTutarTR(tutar) * brutFactor;

  const handleDuzenle = (g) => {
    setEditId(g.id);
    setFirma(g.firma || '');
    setKategori(g.kategori);
    setKalemler([makeKalem(g.kalem || g.aciklama || '', formatTutarTR(g.tutar))]);
    setNotlar(g.aciklama || '');
    setGiderTarihi(g.gider_tarihi);
    setFaturali(g.faturali);
    setFaturaNo(g.fatura_no || '');
    setOdemeYontemi(g.odeme_yontemi || '');
    setOdemeDetay(g.odeme_detay || '');
    setVadeTarihi(g.vade_tarihi || '');
    setLokasyonId(g.lokasyon_id ? String(g.lokasyon_id) : '');
    setTedarikciId(g.tedarikci_id ? String(g.tedarikci_id) : '');
    setCariyeIsle(!!g.cariye_isle);
    // Kayıtlı tutar zaten brüttür → BRUT modda göster (yeniden brüte çevrilmesin).
    setStopajTuru(g.stopaj_turu_kod || '');
    setStopajGiris('BRUT');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleKaydet = async () => {
    if (!firma) { toast.error('Firma seçiniz'); return; }
    if (!kategori) { toast.error('Kategori seçiniz'); return; }
    if (odemeYontemi === 'ACIK_HESAP' && !tedarikciId) {
      toast.error('Açık hesap için tedarikçi seçimi zorunludur');
      return;
    }
    if (cariyeIsle && odemeYontemi !== 'ACIK_HESAP' && !tedarikciId) {
      toast.error('Cariye işlemek için tedarikçi seçiniz');
      return;
    }

    const validKalemler = kalemler.filter(k => k.kalem.trim() && (parseTutarTR(k.tutar) || 0) > 0);
    if (validKalemler.length === 0) {
      toast.error('En az bir gider kalemi ve tutar giriniz');
      return;
    }
    if (netHesaplanamaz) {
      toast.error('Stopaj oranı %100 veya üzeri — net tutardan brüt hesaplanamaz. Brüt moduna geçin.');
      return;
    }
    // Stopaj gider başına tek türdür; çoklu-kalem batch endpoint'i stopaj türetmez.
    // Stopajlı girişte tek kalem zorunlu (yanlış-sessiz kayıt önlenir).
    if (stopajTuru && !editId && validKalemler.length > 1) {
      toast.error('Stopaj yalnız tek kalemli giderde hesaplanır. Stopaj türünü kaldırın veya tek kalem girin.');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('access_token');
      const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };
      const odDetay = odemeYontemi === 'KREDI_KARTI' ? (odemeDetay || null) : null;
      const vdTarihi = odemeYontemi === 'CEK' && vadeTarihi ? vadeTarihi : null;
      let response;

      if (editId) {
        // Edit mode - tek kayit güncelle
        response = await fetch(`${API_URL}/api/finance/gider/${editId}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify({
            firma,
            kategori,
            kalem: validKalemler[0].kalem.trim(),
            aciklama: notlar.trim() || null,
            tutar: toBrut(validKalemler[0].tutar),
            faturali,
            fatura_no: faturali ? faturaNo.trim() || null : null,
            gider_tarihi: giderTarihi,
            odeme_yontemi: odemeYontemi || null,
            odeme_detay: odDetay,
            vade_tarihi: vdTarihi,
            lokasyon_id: lokasyonId ? parseInt(lokasyonId) : null,
            tedarikci_id: tedarikciId ? parseInt(tedarikciId) : null,
            stopaj_turu_kod: stopajTuru || null,
            cariye_isle: odemeYontemi !== 'ACIK_HESAP' && cariyeIsle
          })
        });
      } else if (validKalemler.length === 1) {
        // Tek kalem - normal endpoint
        response = await fetch(`${API_URL}/api/finance/gider`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            firma,
            kategori,
            kalem: validKalemler[0].kalem.trim(),
            aciklama: notlar.trim() || null,
            tutar: toBrut(validKalemler[0].tutar),
            faturali,
            fatura_no: faturali ? faturaNo.trim() || null : null,
            gider_tarihi: giderTarihi,
            odeme_yontemi: odemeYontemi || null,
            odeme_detay: odDetay,
            vade_tarihi: vdTarihi,
            lokasyon_id: lokasyonId ? parseInt(lokasyonId) : null,
            tedarikci_id: tedarikciId ? parseInt(tedarikciId) : null,
            stopaj_turu_kod: stopajTuru || null,
            vergi_tahakkuku: vergiTahakkuku,
            cariye_isle: odemeYontemi !== 'ACIK_HESAP' && cariyeIsle
          })
        });
      } else {
        // Coklu kalem - batch endpoint
        response = await fetch(`${API_URL}/api/finance/gider/batch`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            firma,
            kategori,
            gider_tarihi: giderTarihi,
            odeme_yontemi: odemeYontemi || null,
            odeme_detay: odDetay,
            faturali,
            fatura_no: faturali ? faturaNo.trim() || null : null,
            lokasyon_id: lokasyonId ? parseInt(lokasyonId) : null,
            tedarikci_id: tedarikciId ? parseInt(tedarikciId) : null,
            aciklama: notlar.trim() || null,
            vergi_tahakkuku: vergiTahakkuku,
            cariye_isle: odemeYontemi !== 'ACIK_HESAP' && cariyeIsle,
            kalemler: validKalemler.map(k => ({ kalem: k.kalem.trim(), tutar: toBrut(k.tutar) }))
          })
        });
      }

      const data = await response.json();

      if (response.ok) {
        const firmaLabel = getFirmaLabel(firma);
        const islem = editId ? 'güncellendi' : 'kaydedildi';
        const toplam = validKalemler.reduce((s, k) => s + (parseTutarTR(k.tutar) || 0), 0);
        toast.success(`${firmaLabel}: ${validKalemler.length} kalem, ${formatTutarTR(toplam)} ₺ ${islem}`);
        invalidateQueries(['gider', 'finance', 'dashboard', 'pnl', 'rapor']);
        resetForm();
        if (showGecmis) loadGecmis();
      } else {
        toast.error(data.detail || 'Hata oluştu');
      }
    } catch (err) {
      logError('Gider kaydetme hatası:', err);
      toast.error('Sunucu hatası: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadGecmis = useCallback(async () => {
    setGecmisLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const params = new URLSearchParams({ limit: '200' });
      if (gFirma) params.append('firma', gFirma);
      if (gBas) params.append('baslangic_tarihi', gBas);
      if (gBit) params.append('bitis_tarihi', gBit);
      if (gKat) params.append('kategori', gKat);
      const response = await fetch(`${API_URL}/api/finance/giderler?${params}`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await response.json();
      setGecmis(Array.isArray(data) ? data : data.kayitlar || []);
    } catch {
      setGecmis([]);
    } finally {
      setGecmisLoading(false);
    }
  }, [gFirma, gBas, gBit, gKat]);

  // Sayfa açıldığında ve temel filtreler değiştiğinde kayıtları otomatik yükle.
  useEffect(() => {
    loadGecmis();
  }, [loadGecmis]);

  const handleSil = async (giderId) => {
    if (!window.confirm('Bu gider kaydını silmek istediğinize emin misiniz?')) return;
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_URL}/api/finance/gider/${giderId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Gider silindi');
        loadGecmis();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Silme hatası');
      }
    } catch (err) {
      logError('Gider silme hatası:', err);
      toast.error('Sunucu hatası: ' + err.message);
    }
  };

  const clearGecmisFiltreleri = () => {
    setGBas(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
    setGBit(format(new Date(), 'yyyy-MM-dd'));
    setGFirma('');
    setGKat('');
    setGOdeme('');
    setGAra('');
  };

  const getKategoriLabel = (val) => kategoriler.find(k => k.value === val)?.label || val;
  const getFirmaLabel = (val) => firmalar.find(f => f.kod === val)?.ad || val || '-';
  const getFirmaColor = (val) => {
    const firmaRenkleri = { INGA_BURGER: '#e65100', ALTI_USTU_KOFTE: '#1565c0', TORUNA_KASAP: '#2e7d32', OFIS: '#7b1fa2' };
    return firmaRenkleri[val] || '#6c757d';
  };
  const getKategoriColor = (val) => kategoriler.find(k => k.value === val)?.renk || '#6c757d';
  const getKartLabel = (val) => kartlar.find(k => k.value === val)?.label || val || '';

  const formatGiderTarihi = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return iso;
      return format(d, 'dd.MM.yyyy');
    } catch {
      return iso;
    }
  };

  const tedarikciOptions = tedarikciler.map(t => ({ value: String(t.id), label: t.unvan || t.ad }));

  const filteredGecmis = gecmis.filter(g => {
    if (gBas && g.gider_tarihi < gBas) return false;
    if (gBit && g.gider_tarihi > gBit) return false;
    if (gKat && g.kategori !== gKat) return false;
    if (gOdeme && g.odeme_yontemi !== gOdeme) return false;
    if (gFirma && g.firma !== gFirma) return false;
    if (gAra) {
      const q = normalizeForSearch(gAra);
      const match = searchMatchPre(g.kalem, q) ||
                    searchMatchPre(g.aciklama, q) ||
                    searchMatchPre(g.tedarikci_ad, q);
      if (!match) return false;
    }
    return true;
  });

  const filteredGecmisToplam = filteredGecmis.reduce((s, g) => s + (g.tutar || 0), 0);

  return (
    <div>
      <Header title="Gider Girişi" />
      <div style={{ padding: '20px' }}>

        {/* Düzenleme formu — yeni gider kaydı başka sayfadan yapılır */}
        {editId && (<div style={{
          background: colors.surface, borderRadius: radius.lg, padding: '24px',
          boxShadow: shadows.md, marginBottom: '20px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ margin: 0, color: editId ? '#e65100' : styles.secondary }}>
              Gider Düzenle
            </h3>
            {editId && (
              <button onClick={resetForm} style={{
                background: '#6c757d', color: 'white', border: 'none',
                borderRadius: radius.sm, padding: '6px 16px', cursor: 'pointer', fontSize: '13px'
              }}>İptal</button>
            )}
          </div>

          {/* Enter ile alan-gezme: alanlar arası ilerle, kalem tutarında yeni satır, son alanda kaydet */}
          <EnterNavForm onSubmit={handleKaydet} onRowAdd={addKalem}>

          {/* ── Bölüm 1: Temel Bilgi ── */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ ...typography.overline, margin: '0 0 8px' }}>Temel Bilgi</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '15px' }}>
              <div>
                <label style={labelStyle}>Firma *</label>
                <select value={firma} onChange={(e) => setFirma(e.target.value)}
                  style={{
                    ...inputStyle,
                    borderLeft: firma ? `3px solid ${getFirmaColor(firma)}` : inputStyle.border,
                    color: firma ? getFirmaColor(firma) : colors.textPrimary,
                    fontWeight: firma ? 600 : 400
                  }}>
                  <option value="">Firma Seçiniz</option>
                  {firmalar.filter(f => f.aktif !== false).map(f => (
                    <option key={f.kod} value={f.kod}>{f.ad}{f.tip === 'MASRAF_MERKEZI' ? ' (Masraf Merkezi)' : ''}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Kategori *</label>
                <select value={kategori} onChange={(e) => handleKategoriChange(e.target.value)} style={inputStyle}>
                  <option value="">Kategori Seçiniz</option>
                  {kategoriler.filter(k => k.aktif !== false).map(k => (<option key={k.value} value={k.value}>{k.label}</option>))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Gider Tarihi *</label>
                <input type="date" value={giderTarihi} onChange={(e) => setGiderTarihi(e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Lokasyon</label>
                <select value={lokasyonId} onChange={(e) => setLokasyonId(e.target.value)} style={inputStyle}>
                  <option value="">Seçiniz (Opsiyonel)</option>
                  {lokasyonlar.map(l => (<option key={l.id} value={l.id}>{l.ad}</option>))}
                </select>
              </div>
            </div>
          </div>

          {/* ── Bölüm 2: Gider Kalemleri ── */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ ...typography.overline, margin: '0 0 8px' }}>Gider Kalemleri *</div>
            {/* overflow:visible ŞART — kalem autocomplete önerileri (GiderKalemAutocomplete) bu kartın
                içindedir; overflow:hidden olursa absolute öneri listesi kart sınırında kırpılır (görünmez). */}
            <div style={{ border: `1px solid ${colors.border}`, borderRadius: radius.md, overflow: 'visible' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: colors.backgroundAlt }}>
                    <th style={{ padding: '8px 12px', textAlign: 'center', width: '40px', fontSize: '12px', color: colors.textSecondary }}>#</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '12px', color: colors.textSecondary }}>Gider Kalemi</th>
                    <th style={{ padding: '8px 12px', textAlign: 'right', width: '160px', fontSize: '12px', color: colors.textSecondary }}>Tutar (₺)</th>
                    <th style={{ padding: '8px 12px', textAlign: 'center', width: '40px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {kalemler.map((k, i) => (
                    <tr key={k.uid} data-enter-row style={{ borderTop: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '6px 12px', textAlign: 'center', color: colors.textTertiary, fontSize: '13px' }}>{i + 1}</td>
                      <td style={{ padding: '6px 8px' }}>
                        <GiderKalemAutocomplete
                          value={k.kalem}
                          onChange={(val) => updateKalem(i, 'kalem', val)}
                          kategori={kategori}
                        />
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <input
                          type="text"
                          inputMode="decimal"
                          data-enter-nav="row-add"
                          value={k.tutar}
                          onChange={(e) => updateKalem(i, 'tutar', e.target.value)}
                          placeholder="0,00"
                          style={{ ...inputStyle, textAlign: 'right', fontWeight: 600 }}
                        />
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                        {kalemler.length > 1 && (
                          <button onClick={() => removeKalem(i)} style={{
                            background: '#f8d7da', color: '#721c24', border: 'none',
                            borderRadius: radius.sm, padding: '4px 8px', cursor: 'pointer', fontSize: '14px'
                          }}>X</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '8px 12px', borderTop: `1px solid ${colors.border}`, background: colors.backgroundAlt
              }}>
                {!editId && (
                  <button onClick={addKalem} style={{
                    background: 'transparent', color: styles.primary, border: `1px dashed ${styles.primary}`,
                    borderRadius: radius.sm, padding: '6px 16px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold'
                  }}>+ Satır Ekle</button>
                )}
                {editId && <div></div>}
                <div style={{
                  background: colors.secondary, color: '#fff', padding: '6px 14px',
                  borderRadius: radius.sm, fontSize: '14px', fontWeight: 700
                }}>
                  Toplam: {formatTutarTR(genelToplam)} ₺
                </div>
              </div>
            </div>
          </div>

          {/* ── Bölüm 2b: Stopaj ── */}
          {/* Stopaj türü seçiliyse VEYA kategorinin varsayılan türü varsa göster. */}
          {(() => {
            const kat = kategoriler.find(k => k.value === kategori);
            const stopajGoster = !!stopajTuru || !!(kat && kat.varsayilan_stopaj_turu_kod);
            if (!stopajGoster) return null;
            return (
              <div style={{ marginBottom: '20px' }}>
                <div style={{ ...typography.overline, margin: '0 0 8px' }}>Stopaj</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '15px', alignItems: 'flex-start' }}>
                  <div>
                    <label style={labelStyle}>Stopaj Türü</label>
                    <select value={stopajTuru} onChange={(e) => setStopajTuru(e.target.value)} style={inputStyle}>
                      <option value="">Stopaj yok</option>
                      {stopajTurleri.map(s => (
                        <option key={s.kod} value={s.kod}>{s.ad} (%{formatTutarTR(s.oran)})</option>
                      ))}
                    </select>
                  </div>
                  {stopajTuru && (
                    <div>
                      <label style={labelStyle}>Girilen Tutar</label>
                      <div style={{ display: 'flex', gap: '14px', alignItems: 'center', padding: '8px 0' }}>
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '13px', cursor: 'pointer', color: colors.textPrimary }}>
                          <input type="radio" name="stopajGiris" value="BRUT" checked={stopajGiris === 'BRUT'} onChange={() => setStopajGiris('BRUT')} />
                          Brüt
                        </label>
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '13px', cursor: 'pointer', color: colors.textPrimary }}>
                          <input type="radio" name="stopajGiris" value="NET" checked={stopajGiris === 'NET'} onChange={() => setStopajGiris('NET')} />
                          Net (ele geçen)
                        </label>
                      </div>
                    </div>
                  )}
                </div>
                {stopajTuru && netHesaplanamaz && (
                  <div style={{ marginTop: '10px', padding: '8px 12px', background: '#fdecea', color: '#b71c1c', borderRadius: radius.sm, fontSize: '13px' }}>
                    Stopaj oranı %{formatTutarTR(stopajOran)} olduğundan net tutardan brüt hesaplanamaz. Lütfen Brüt moduna geçin.
                  </div>
                )}
                {stopajTuru && genelToplam > 0 && !netHesaplanamaz && (
                  <div style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    <div style={{ flex: 1, minWidth: '150px', padding: '10px 12px', background: colors.backgroundAlt, borderRadius: radius.sm, border: `1px solid ${colors.border}` }}>
                      <div style={{ fontSize: '11px', color: colors.textSecondary }}>Brüt</div>
                      <div style={{ fontSize: '15px', fontWeight: 700, color: colors.textPrimary, fontVariantNumeric: 'tabular-nums' }}>{formatTutarTR(stopajBrut)} ₺</div>
                    </div>
                    <div style={{ flex: 1, minWidth: '150px', padding: '10px 12px', background: '#fff8e1', borderRadius: radius.sm, border: '1px solid #ffe082' }}>
                      <div style={{ fontSize: '11px', color: '#8d6e00' }}>Stopaj (%{formatTutarTR(stopajOran)})</div>
                      <div style={{ fontSize: '15px', fontWeight: 700, color: '#8d6e00', fontVariantNumeric: 'tabular-nums' }}>{formatTutarTR(stopajTutar)} ₺</div>
                    </div>
                    <div style={{ flex: 1, minWidth: '150px', padding: '10px 12px', background: '#e8f5e9', borderRadius: radius.sm, border: '1px solid #a5d6a7' }}>
                      <div style={{ fontSize: '11px', color: '#1b5e20' }}>Karşı tarafa ödenen (net)</div>
                      <div style={{ fontSize: '15px', fontWeight: 700, color: '#1b5e20', fontVariantNumeric: 'tabular-nums' }}>{formatTutarTR(stopajNet)} ₺</div>
                    </div>
                  </div>
                )}
                {stopajTuru && (
                  <div style={{ marginTop: '8px', fontSize: '12px', color: colors.textTertiary }}>
                    Gider brüt tutarla kaydedilir; stopaj türünden hesaplanır.
                  </div>
                )}
              </div>
            );
          })()}

          {/* ── Bölüm 3: Ödeme & Cari ── */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ ...typography.overline, margin: '0 0 8px' }}>Ödeme & Cari</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '15px' }}>
              <div>
                <label style={labelStyle}>Ödeme Yöntemi</label>
                <select value={odemeYontemi} onChange={(e) => { setOdemeYontemi(e.target.value); if (e.target.value !== 'KREDI_KARTI') setOdemeDetay(''); if (e.target.value !== 'CEK') setVadeTarihi(''); }} style={inputStyle}>
                  {ODEME_OPTIONS.map(o => (<option key={o.value} value={o.value}>{o.label}</option>))}
                </select>
                {odemeYontemi === 'KREDI_KARTI' && (
                  <select value={odemeDetay} onChange={(e) => setOdemeDetay(e.target.value)}
                    style={{ ...inputStyle, marginTop: '8px', borderColor: '#ff9800' }}>
                    <option value="">Kart Seçiniz</option>
                    {kartlar.filter(k => k.aktif !== false).map(k => (<option key={k.value} value={k.value}>{k.label}</option>))}
                  </select>
                )}
                {odemeYontemi === 'CEK' && (
                  <div style={{ marginTop: '8px' }}>
                    <label style={{ ...labelStyle, color: '#e65100' }}>Çek Vade Tarihi</label>
                    <input type="date" value={vadeTarihi} onChange={(e) => setVadeTarihi(e.target.value)}
                      style={{ ...inputStyle, borderColor: '#ff9800' }} />
                  </div>
                )}
              </div>
              <div>
                <label style={labelStyle}>
                  Tedarikçi
                  {odemeYontemi === 'ACIK_HESAP' && <span style={{ color: '#dc2626', marginLeft: 4 }}>*</span>}
                </label>
                <SearchableSelect
                  options={tedarikciOptions}
                  value={tedarikciId}
                  onChange={setTedarikciId}
                  placeholder={odemeYontemi === 'ACIK_HESAP' ? 'Açık hesap için zorunlu — ara…' : 'Tedarikçi ara… (opsiyonel)'}
                  invalid={odemeYontemi === 'ACIK_HESAP' && !tedarikciId}
                />
              </div>
              {odemeYontemi && odemeYontemi !== 'ACIK_HESAP' && (
                <div>
                  <label style={labelStyle}>Cari Hesap</label>
                  <div>
                    <Toggle
                      checked={cariyeIsle}
                      onChange={(e) => setCariyeIsle(e.target.checked)}
                      label="Cariye işle"
                      title="Bu alımı tedarikçinin cari ekstresinde 'ödenmiş alış' olarak göster. Ödeme zaten yapıldığı için bakiyeyi değiştirmez; sadece tedarikçiden ne aldığınızın kaydı görünür."
                      activeColor="#7b1fa2"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── Bölüm 4: Belge & Muhasebe ── */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ ...typography.overline, margin: '0 0 8px' }}>Belge & Muhasebe</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center', marginBottom: '15px' }}>
              <Toggle
                checked={faturali}
                onChange={(e) => { setFaturali(e.target.checked); if (!e.target.checked) setFaturaNo(''); }}
                label="Faturalı"
                activeColor={colors.success}
              />
              <Toggle
                checked={vergiTahakkuku}
                onChange={(e) => setVergiTahakkuku(e.target.checked)}
                label="Vergi/SGK Tahakkuku"
                title="Maaştan doğan SGK/muhtasar gibi tahakkuklar — ödeme yapılmamış olsa bile gider ilgili aya yazılır (tarihi o aydan seçin)"
                activeColor={colors.warning}
              />
            </div>
            {faturali && (
              <div style={{ marginBottom: '15px' }}>
                <label style={labelStyle}>Fatura No</label>
                <input type="text" value={faturaNo} onChange={(e) => setFaturaNo(e.target.value)}
                  placeholder="Fatura numarası…" style={{ ...inputStyle, maxWidth: '400px' }} />
              </div>
            )}
            <div>
              <label style={labelStyle}>Notlar (opsiyonel)</label>
              <input
                type="text"
                value={notlar}
                onChange={(e) => setNotlar(e.target.value)}
                placeholder="Ek açıklama veya not…"
                style={inputStyle}
              />
            </div>
          </div>

          {/* Özet & Kaydet */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginTop: '20px', paddingTop: '15px', borderTop: `1px solid ${colors.border}`
          }}>
            <div style={{ fontSize: '13px', color: colors.textSecondary }}>
              {firma && kategori && genelToplam > 0 && (
                <span>
                  <strong style={{ color: getFirmaColor(firma) }}>{getFirmaLabel(firma)}</strong>
                  {' / '}<strong>{getKategoriLabel(kategori)}</strong>
                  {' - '}{kalemler.filter(k => k.kalem.trim()).length} kalem, {formatTutarTR(genelToplam)} ₺
                  {faturali && ' (Faturalı)'}
                  {odemeYontemi && ` | ${ODEME_OPTIONS.find(o => o.value === odemeYontemi)?.label || odemeYontemi}`}
                  {odemeYontemi === 'KREDI_KARTI' && odemeDetay && ` (${getKartLabel(odemeDetay)})`}
                </span>
              )}
            </div>
            <button onClick={handleKaydet} disabled={loading} style={{
              background: loading ? '#ccc' : styles.primary, color: 'white', border: 'none',
              borderRadius: radius.md, padding: '12px 40px', fontSize: '16px', fontWeight: 'bold',
              cursor: loading ? 'default' : 'pointer'
            }}>
              {loading ? 'Kaydediliyor…' : (editId ? 'Güncelle' : 'Kaydet')}
            </button>
          </div>

          </EnterNavForm>
        </div>)}

        {/* Gider Kayıtları */}
        <div style={{
          background: colors.surface, borderRadius: radius.lg, boxShadow: shadows.sm, overflow: 'hidden',
        }}>
          <div
            style={{
              width: '100%', padding: '16px 20px', boxSizing: 'border-box',
              fontSize: '15px', fontWeight: 700, color: colors.textPrimary, textAlign: 'left',
            }}>
            Gider Kayıtları {gecmis.length > 0 && <span style={{ fontSize: '12px', color: colors.textTertiary, fontWeight: 400 }}>({filteredGecmis.length} kayıt)</span>}
          </div>

          {showGecmis && (
            <div style={{ padding: '0 20px 20px' }}>
              {/* Filter bar */}
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px', alignItems: 'flex-end' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', color: colors.textSecondary, marginBottom: '4px' }}>Başlangıç</label>
                  <input type="date" value={gBas}
                    onChange={e => setGBas(e.target.value)}
                    style={{ padding: '7px 10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', color: colors.textSecondary, marginBottom: '4px' }}>Bitiş</label>
                  <input type="date" value={gBit}
                    onChange={e => setGBit(e.target.value)}
                    style={{ padding: '7px 10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', color: colors.textSecondary, marginBottom: '4px' }}>Firma</label>
                  <select value={gFirma} onChange={e => setGFirma(e.target.value)}
                    style={{ padding: '7px 10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px' }}>
                    <option value="">Tümü</option>
                    {firmalar.filter(f => f.aktif !== false).map(f => <option key={f.kod} value={f.kod}>{f.ad}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', color: colors.textSecondary, marginBottom: '4px' }}>Kategori</label>
                  <select value={gKat}
                    onChange={e => setGKat(e.target.value)}
                    style={{ padding: '7px 10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px' }}>
                    <option value="">Tümü</option>
                    {kategoriler.filter(k => k.aktif !== false).map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', color: colors.textSecondary, marginBottom: '4px' }}>Ödeme Yöntemi</label>
                  <select value={gOdeme}
                    onChange={e => setGOdeme(e.target.value)}
                    style={{ padding: '7px 10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px' }}>
                    <option value="">Tümü</option>
                    {ODEME_OPTIONS.filter(o => o.value).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div style={{ flex: 1, minWidth: '180px' }}>
                  <label style={{ display: 'block', fontSize: '11px', color: colors.textSecondary, marginBottom: '4px' }}>Kalem / Açıklama Ara</label>
                  <input type="text" value={gAra} placeholder="Ara…"
                    onChange={e => setGAra(e.target.value)}
                    style={{ width: '100%', padding: '7px 10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px', boxSizing: 'border-box' }} />
                </div>
                <button onClick={loadGecmis} disabled={gecmisLoading}
                  style={{
                    padding: '8px 20px', background: gecmisLoading ? '#ccc' : colors.primary, color: 'white',
                    border: 'none', borderRadius: radius.sm, cursor: gecmisLoading ? 'default' : 'pointer',
                    fontWeight: 600, fontSize: '13px',
                  }}>
                  {gecmisLoading ? 'Yükleniyor…' : 'Yenile'}
                </button>
                <button onClick={clearGecmisFiltreleri}
                  style={{
                    padding: '8px 16px', background: 'transparent', color: colors.textSecondary,
                    border: `1px solid ${colors.border}`, borderRadius: radius.sm, cursor: 'pointer',
                    fontWeight: 600, fontSize: '13px',
                  }}>
                  Filtreleri temizle
                </button>
              </div>

              {/* Table */}
              {gecmisLoading ? (
                <div style={{ textAlign: 'center', padding: '20px', color: colors.textSecondary }}>Yükleniyor…</div>
              ) : filteredGecmis.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px', color: colors.textTertiary }}>
                  {gecmis.length === 0 ? 'Henüz gider kaydı yapılmamış' : 'Filtreye uyan kayıt bulunamadı'}
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: colors.backgroundAlt }}>
                        {['Tarih', 'Firma', 'Kategori', 'Gider Kalemi', 'Tutar', 'Fatura', 'Ödeme', 'İşlem'].map(h => (
                          <th key={h} style={{
                            padding: '10px 12px', fontSize: '11px', fontWeight: 600,
                            color: colors.textTertiary, letterSpacing: '0.04em',
                            textAlign: h === 'Tutar' ? 'right' : 'left',
                            whiteSpace: 'nowrap',
                            borderBottom: `2px solid ${colors.border}`,
                            ...(h === 'İşlem' ? { width: '90px' } : {}),
                          }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredGecmis.map(g => (
                        <tr key={g.id}
                            onClick={() => handleDuzenle(g)}
                            style={{
                              borderBottom: `1px solid ${colors.border}`,
                              background: editId === g.id ? '#fff3e0' : 'transparent',
                              cursor: 'pointer',
                              transition: 'background 0.15s'
                            }}
                            onMouseEnter={e => { if (editId !== g.id) e.currentTarget.style.background = '#e3f2fd'; }}
                            onMouseLeave={e => { if (editId !== g.id) e.currentTarget.style.background = 'transparent'; }}>
                          <td style={tdStyle}>{formatGiderTarihi(g.gider_tarihi)}</td>
                          <td style={tdStyle}>
                            <span style={{
                              background: getFirmaColor(g.firma), color: 'white',
                              padding: '2px 8px', borderRadius: radius.lg, fontSize: '11px',
                              fontWeight: 'bold', whiteSpace: 'nowrap'
                            }}>{getFirmaLabel(g.firma)}</span>
                          </td>
                          <td style={tdStyle}>
                            <span style={{
                              background: getKategoriColor(g.kategori), color: 'white',
                              padding: '2px 8px', borderRadius: radius.lg, fontSize: '11px', fontWeight: 'bold'
                            }}>{getKategoriLabel(g.kategori)}</span>
                          </td>
                          <td style={tdStyle}>{g.kalem || g.aciklama}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, color: colors.textPrimary, fontVariantNumeric: 'tabular-nums' }}>
                            {formatTutarTR(g.tutar)} ₺
                          </td>
                          <td style={tdStyle}>
                            {g.faturali ? (
                              <span style={{ color: '#28a745', fontWeight: 'bold', fontSize: '12px' }}>{g.fatura_no || 'Evet'}</span>
                            ) : (
                              <span style={{ color: colors.textTertiary, fontSize: '12px' }}>Hayır</span>
                            )}
                          </td>
                          <td style={{ ...tdStyle, fontSize: '12px', color: colors.textSecondary }}>
                            {g.odeme_yontemi ? (ODEME_OPTIONS.find(o => o.value === g.odeme_yontemi)?.label || g.odeme_yontemi) : '-'}
                            {g.odeme_detay && <span style={{ display: 'block', fontSize: '11px', color: colors.textTertiary }}>{getKartLabel(g.odeme_detay)}</span>}
                          </td>
                          <td style={{ ...tdStyle, whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                            <button onClick={() => handleDuzenle(g)} title="Düzenle" style={{
                              background: editId === g.id ? '#e65100' : '#1565c0', color: 'white',
                              border: 'none', borderRadius: radius.sm, padding: '6px 14px',
                              cursor: 'pointer', fontSize: '12px', fontWeight: 'bold', marginRight: '6px'
                            }}>Düzenle</button>
                            <button onClick={() => handleSil(g.id)} title="Sil" style={{
                              background: '#e53935', color: 'white', border: 'none',
                              borderRadius: radius.sm, padding: '6px 10px', cursor: 'pointer', fontSize: '12px'
                            }}>Sil</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    {filteredGecmis.length > 0 && (
                      <tfoot>
                        <tr style={{ background: colors.backgroundAlt, borderTop: `2px solid ${colors.border}` }}>
                          <td colSpan={4} style={{ padding: '10px 12px', fontWeight: 700, fontSize: '13px' }}>Toplam ({filteredGecmis.length} kayıt)</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600, fontSize: '14px', color: colors.textPrimary, fontVariantNumeric: 'tabular-nums' }}>
                            {formatTutarTR(filteredGecmisToplam)} ₺
                          </td>
                          <td colSpan={3} style={{ padding: '10px 12px' }}></td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Stiller
const labelStyle = {
  display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold', color: '#555'
};

const inputStyle = {
  width: '100%', padding: '8px 12px', border: `1px solid ${colors.border}`,
  borderRadius: radius.sm, fontSize: '14px', boxSizing: 'border-box'
};

const tdStyle = {
  padding: '8px 12px', fontSize: '14px', verticalAlign: 'middle'
};

export default GiderGirisiPage;
